import "../examine/featureTable.css";
import { useState } from "react";
import { IpaType } from "../../phonemes/ipa";
import { PhonemeList, segmentPhonemes } from "../../phonemes/segmentPhonemes";
import { FeaturesForIpa } from "../examine/FeaturesForIpa";
import { ReplaceSymbols } from "../examine/ReplaceSymbols";
import { MultiExpando } from "../widgets/MultiExpando";
import { PhonemeLengths } from "../examine/PhonemeLengths";
import { FixupRules } from "../examine/fixupRules";
import { RuleSet } from "../../rules/ruleSet";
import { ColumnAlign } from "../widgets/ColumnAlign";
import { almostIpa } from "../../phonemes/cleanupIpa";

/** options for seeing IPA features and making rules for modifying input IPA */
export function IpaOptions(props: {
  /** IPA to show & infer rules for */
  ipa: IpaType,
  /** IPA as spoken by speech synthesizer */
  asSpoken: IpaType,
  /** called when the input-IPA => output-IPA rule gets edited */
  setRules: (rules: RuleSet) => void,
}) {
  const [cached, setCached] = useState(props.ipa);
  const [segments, setSegments] = useState(getSegments(props.ipa));
  const [fixupRules] = useState(() => new FixupRules());
  const [rulesStr, setRulesStr] = useState('');
  const [stress, setStress] = useState(0);
  const [phonemeLengths, setPhonemeLengths] = useState<Record<IpaType, number>>({});

  if (cached !== props.ipa) {
    setCached(props.ipa);
    const newSegments = getSegments(props.ipa)
    setSegments(newSegments);
    if (addObviousRules(newSegments, fixupRules)) {
      setRulesStr(fixupRules.getRulesAsString());
      props.setRules(fixupRules.getRules());
    }
  }

  function addChange(from: string, to: string) {
    fixupRules.addChange(from, to);
    setRulesStr(fixupRules.getRulesAsString());
    props.setRules(fixupRules.getRules());
  }
  function hasChange(from: string, to: string) {
    return fixupRules.hasChange(from, to);
  }
  function getTo(from: string) {
    return fixupRules.getTo(from);
  }

  function addStress(syllable: number) {
    setStress(syllable);
    fixupRules.addStress(syllable);
    setRulesStr(fixupRules.getRulesAsString());
    props.setRules(fixupRules.getRules());
  }

  function setLength(phoneme: IpaType, length: number) {
    fixupRules.setPhonemeLength(phoneme, length);
    setPhonemeLengths(fixupRules.getLengthMap());
    setRulesStr(fixupRules.getRulesAsString());
    props.setRules(fixupRules.getRules());
  }
  function clearLengths() {
    fixupRules.clearPhonemeLengths();
    setPhonemeLengths(fixupRules.getLengthMap());
    setRulesStr(fixupRules.getRulesAsString());
    props.setRules(fixupRules.getRules());
  }

  function clearRules() {
    fixupRules.clear();
    setPhonemeLengths({});
    setStress(0);
    setRulesStr('');
    props.setRules(fixupRules.getRules());
  }

  return (<div style={{ display: 'grid', justifyContent: 'center' }}>
    <MultiExpando title="show:"
      labels={['spoken', 'fixes', 'diphthongs', 'combos', 'lengths', 'rules', 'features']}
      expanded={[false, true, true, true, false, true, false]}>
      {[
        <span key="spoken">
          <br />as spoken by speech synthesizer<br />
          <textarea cols={80} rows={3} spellCheck={false} value={props.asSpoken} readOnly={true} />
        </span>,
        <span key="fixes">
          <ReplaceSymbols replacements={['unknown', 'stress']} segments={segments} replace={addChange} hasRule={hasChange} getTo={getTo} stress={stress} addStress={addStress} />
        </span>,
        <span key="diphthongs">
          <ReplaceSymbols replacements={['thongs']} segments={segments} replace={addChange} hasRule={hasChange} />
        </span>,
        <span key="combos">
          <ReplaceSymbols replacements={['combine']} segments={segments} replace={addChange} hasRule={hasChange} />
        </span>,
        <span key="length">
          <PhonemeLengths lengthMap={phonemeLengths} setLength={setLength} clearLengths={clearLengths} />
        </span>,
        <span key="rules"><RulesArea rulesStr={rulesStr} clearRules={() => clearRules()} /><br /></span>,
        <FeaturesForIpa segments={segments} key="features" />,
      ]}
    </MultiExpando>
  </div>);
}

/** only show rules if there are some */
function RulesArea(props: { rulesStr: string, clearRules: () => void }) {
  if (props.rulesStr.length === 0) { return <></>; }
  return (<ColumnAlign>{[
    <div key="1">
      <div className='smallTitle'>Rules</div>
      <button onClick={() => props.clearRules()}>clear rules</button>
    </div>,
    <textarea cols={30} rows={10} spellCheck={false} value={props.rulesStr} readOnly={true} key="2" />
  ]}</ColumnAlign>);
}

function getSegments(ipa: IpaType) {
  const normalized = ipa.replaceAll(/\s/g, ' ');
  return segmentPhonemes(normalized, 'ipaPlus');
}

/** add fixup rules for the most obvious non-IPA, return true if any rules added */
function addObviousRules(segments: PhonemeList, fixupRules: FixupRules): boolean {
  let added = false;
  for (const phoneme of segments.phonemes) {
    const p = phoneme.phoneme;
    if (isAlmostIpa(p)) {
      const to = fixupRules.getTo(p);
      if (to === null) {
        // there's a simple fix, but there isn't yet a rule, so add one
        fixupRules.addChange(p, almostIpa[p]);
        added = true;
      }
    }
  }
  return added;
}

/** check if phoneme is something obvious to convert */
function isAlmostIpa(p: IpaType): boolean {
  if (quickCheck === null) {
    quickCheck = new Set();
    const almostList = Object.getOwnPropertyNames(almostIpa);
    almostList.forEach(p => quickCheck?.add(p));
  }
  return quickCheck.has(p);
}
let quickCheck: Set<IpaType> | null = null;
